import { createFileRoute, Link } from "@tanstack/react-router";
import { abs } from "@/lib/site";
import { SiteHeader, SiteFooter } from "@/components/SiteChrome";
import { FontGenerator } from "@/components/FontGenerator";
import { FANCY_STYLES, STYLE_CATEGORIES, UTILITY_TOOLS } from "@/lib/text-generators";

const STYLE_COUNT = FANCY_STYLES.length;

export const Route = createFileRoute("/font-generator/")({
  head: () => ({
    meta: [
      {
        title: `Font Generator — ${STYLE_COUNT} fancy fonts to copy & paste`,
      },
      {
        name: "description",
        content: `Free font generator: type once and copy from ${STYLE_COUNT} fancy text styles — cursive, bold, gothic, bubble, small caps, aesthetic and more. Real Unicode characters that paste into Instagram, TikTok, Discord and bios. Live preview, one-click copy.`,
      },
      { property: "og:title", content: `Font Generator — ${STYLE_COUNT} fancy fonts` },
      {
        property: "og:description",
        content: `Type once, copy from ${STYLE_COUNT} fancy text styles. Real Unicode, pastes anywhere.`,
      },
      { property: "og:url", content: abs("/font-generator") },
      { property: "og:type", content: "website" },
    ],
    links: [{ rel: "canonical", href: abs("/font-generator") }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "WebApplication",
          name: "Font Generator",
          applicationCategory: "UtilityApplication",
          operatingSystem: "Any",
          url: abs("/font-generator"),
          description: `Convert plain text into ${STYLE_COUNT} fancy Unicode font styles, copy and paste anywhere.`,
          offers: { "@type": "Offer", price: "0", priceCurrency: "USD" },
        }),
      },
    ],
  }),
  component: FontsHub,
});

function FontsHub() {
  return (
    <div className="flex min-h-screen flex-col">
      <SiteHeader />
      <main className="mx-auto w-full max-w-3xl flex-1 px-4 py-12 sm:px-6 sm:py-16">
        <nav aria-label="Breadcrumb" className="text-sm text-muted-foreground">
          <Link to="/" className="hover:text-foreground">
            Home
          </Link>
          <span aria-hidden="true"> / </span>
          <span className="text-foreground/80">Font generator</span>
        </nav>

        <h1 className="mt-3 font-display text-4xl font-bold sm:text-5xl">
          Font <span className="text-gradient">generator</span>
        </h1>
        <p className="mt-4 text-lg text-foreground/85">
          Type once and copy your words in {STYLE_COUNT} fancy text styles — looping cursive,
          heavy bold, blackletter gothic, bubble letters, small caps, aesthetic spacing and dozens of
          decorated designs. Tap any style to copy it.
        </p>
        <p className="mt-3 text-foreground/75">
          Nothing here is an image or an installed font. Every style is built from genuine Unicode
          characters, so your styled text pastes cleanly into an Instagram or TikTok bio, a Discord
          name, a tweet, a YouTube comment or a document — and survives the trip.
        </p>

        <FontGenerator defaultValue="Fancy Text" />

        <section aria-labelledby="how-heading" className="mt-14">
          <h2 id="how-heading" className="font-display text-2xl font-bold">
            How the font generator works
          </h2>
          <p className="mt-3 text-foreground/85">
            A “font” you copy into a plain-text box isn’t really a font at all — there’s nowhere to
            attach one. Instead, each style maps your letters to alternate Unicode characters that
            already look bold, italic, cursive or boxed. Because every glyph is its own real,
            assigned character (mostly from the Mathematical Alphanumeric Symbols, Enclosed
            Alphanumerics and related blocks), the result keeps its look wherever plain text is
            allowed. Letters a particular style never encoded — small caps has no X, for example —
            are left as-is rather than faked, and every codepoint on this page is verified against the
            Unicode database at build time, so you’ll never copy a broken □ box.
          </p>
        </section>

        <section aria-labelledby="cat-heading" className="mt-12">
          <h2 id="cat-heading" className="font-display text-2xl font-bold">
            What’s inside
          </h2>
          <ul className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2">
            {STYLE_CATEGORIES.map((c) => (
              <li key={c.id}>
                <a
                  href={`#cat-${c.id}`}
                  className="glass block h-full rounded-lg px-4 py-3 transition-colors hover:border-primary/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                >
                  <span className="font-display font-semibold">{c.name}</span>
                  <span className="mt-0.5 block text-sm text-muted-foreground">{c.blurb}</span>
                </a>
              </li>
            ))}
          </ul>
        </section>

        <nav aria-labelledby="more-heading" className="mt-12 border-t border-border pt-6">
          <h2 id="more-heading" className="text-sm font-medium text-muted-foreground">
            More text tools
          </h2>
          <ul className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-3">
            {UTILITY_TOOLS.map((t) => (
              <li key={t.slug}>
                <Link
                  to="/font-generator/$slug"
                  params={{ slug: t.slug }}
                  className="glass flex h-full flex-col rounded-xl px-4 py-4 transition-colors hover:border-primary/40"
                >
                  <span className="break-words text-lg">{t.sample}</span>
                  <span className="mt-2 font-display text-sm font-semibold">{t.name}</span>
                </Link>
              </li>
            ))}
          </ul>
        </nav>
      </main>
      <SiteFooter />
    </div>
  );
}
